import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  useTeamInfo, useInvites, useTeamMembers,
  createInvite, deleteInvite, rotateJoinToken, createCheckoutSession,
} from '../tareas/api';
import { planLimitHint } from '../billing/limits';
import type { PlanLimitHint } from '../billing/limits';
import UpgradeBanner from '../billing/UpgradeBanner';
import { useDialog } from '../ui/Dialogs';

export default function TeamInvites() {
  const { profile } = useAuth();
  const { confirm } = useDialog();
  const { team, loading } = useTeamInfo();
  const { members } = useTeamMembers();
  const { invites } = useInvites();

  const [joinToken, setJoinToken] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const [limitHint, setLimitHint] = useState<PlanLimitHint | null>(null);

  if (loading || !team) {
    return (
      <section style={styles.card}>
        <h2 style={styles.cardTitle}>Invitaciones</h2>
        <p style={styles.muted}>{loading ? 'Cargando…' : 'No se pudo cargar el equipo.'}</p>
      </section>
    );
  }

  const myRole = members.find((m) => m.id === profile?.id)?.role;
  const canManage = myRole === 'owner' || myRole === 'admin';
  const token = joinToken ?? team.join_token;
  const link = `${window.location.origin}/join/${team.id}/${token}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* el usuario puede copiar manualmente */ }
  };

  const rotate = async () => {
    const ok = await confirm({
      title: 'Regenerar enlace de invitación',
      message: 'Esto invalidará el enlace de invitación actual. ¿Continuar?',
      confirmLabel: 'Regenerar',
    });
    if (!ok) return;
    setBusy(true); setError('');
    try { setJoinToken(await rotateJoinToken(team.id)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Error'); }
    finally { setBusy(false); }
  };

  const invite = async () => {
    if (!email.includes('@')) { setError('Introduce un email válido'); return; }
    setBusy(true); setError(''); setSent(null); setLimitHint(null);
    try {
      const dest = email.trim();
      await createInvite(dest);
      setEmail('');
      setSent(dest);
      setTimeout(() => setSent(null), 4000);
    }
    catch (e) {
      const hint = planLimitHint(e);
      if (hint) setLimitHint(hint);
      else setError(e instanceof Error ? e.message : 'Error');
    }
    finally { setBusy(false); }
  };

  return (
    <section style={styles.card}>
      <h2 style={styles.cardTitle}>Invitaciones</h2>
      <p style={styles.help}>
        Comparte el enlace o invita por email para que más personas se unan a tu equipo.
      </p>

      {/* Enlace de invitación */}
      <div style={{ ...styles.row, alignItems: 'flex-start' }}>
        <span style={styles.label}>Enlace</span>
        <div style={{ flex: 1 }}>
          <div style={styles.linkRow}>
            <input readOnly value={link} style={{ ...styles.input, flex: 1, fontSize: 12.5 }} onFocus={(e) => e.currentTarget.select()} />
            <button onClick={copy} style={styles.btnPrimary}>{copied ? '¡Copiado!' : 'Copiar'}</button>
          </div>
          {canManage && (
            <button onClick={rotate} disabled={busy} style={{ ...styles.btnGhost, marginTop: 8 }}>
              Generar enlace nuevo
            </button>
          )}
        </div>
      </div>

      {/* Invitar por email (admins) */}
      {canManage && (
        <div style={{ ...styles.row, marginTop: 14 }}>
          <span style={styles.label}>Invitar</span>
          <input
            type="email" placeholder="email@empresa.com" value={email}
            onChange={(e) => setEmail(e.target.value)} style={{ ...styles.input, flex: 1 }}
          />
          <button onClick={invite} disabled={busy} style={styles.btnPrimary}>Enviar</button>
        </div>
      )}

      {sent && <p style={styles.ok}>Invitación enviada a {sent} ✓</p>}
      {error && <p style={styles.error}>{error}</p>}
      {limitHint && (
        <div style={{ marginTop: 12 }}>
          <UpgradeBanner
            hint={limitHint}
            onUpgrade={async () => {
              try { window.location.href = await createCheckoutSession(team.id, 'year'); }
              catch (e) { setError(e instanceof Error ? e.message : 'No se pudo iniciar el pago'); }
            }}
          />
        </div>
      )}

      {/* Invitaciones enviadas, con su estado */}
      {invites.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={styles.sectionLabel}>Invitaciones ({invites.length})</div>
          <div style={styles.list}>
            {invites.map((inv) => {
              const status = inviteStatus(inv);
              return (
                <div key={inv.id} style={styles.inviteRow}>
                  <span style={{ flex: 1, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.email}</span>
                  <span style={{ ...styles.statusPill, ...STATUS_STYLE[status] }}>{STATUS_LABEL[status]}</span>
                  {canManage && status === 'pendiente' && (
                    <button onClick={() => deleteInvite(inv.id)} style={styles.btnDanger}>Cancelar</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

type InviteState = 'aceptada' | 'pendiente' | 'caducada';

function inviteStatus(inv: { accepted_at: string | null; expires_at: string }): InviteState {
  if (inv.accepted_at) return 'aceptada';
  if (inv.expires_at && new Date(inv.expires_at).getTime() < Date.now()) return 'caducada';
  return 'pendiente';
}

const STATUS_LABEL: Record<InviteState, string> = {
  aceptada: 'Aceptada',
  pendiente: 'Pendiente',
  caducada: 'Caducada',
};

const STATUS_STYLE: Record<InviteState, CSSProperties> = {
  aceptada: { background: '#dcfce7', color: '#15803d' },
  pendiente: { background: '#fef3c7', color: '#b45309' },
  caducada: { background: '#f1f5f9', color: '#94a3b8' },
};

const styles: Record<string, CSSProperties> = {
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22, marginBottom: 18 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 },
  help: { fontSize: 13, color: '#64748b', margin: '8px 0 16px', lineHeight: 1.5 },
  row: { display: 'flex', alignItems: 'center', gap: 10 },
  label: { width: 70, fontSize: 13, fontWeight: 600, color: '#64748b' },
  input: { border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', background: '#fff' },
  linkRow: { display: 'flex', gap: 8, alignItems: 'center' },
  btnPrimary: { background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnGhost: { background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#475569', fontSize: 13, fontWeight: 600 },
  btnDanger: { background: 'transparent', border: '1px solid #fecaca', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', color: '#dc2626', fontSize: 12.5, fontWeight: 600 },
  sectionLabel: { fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
  list: { display: 'flex', flexDirection: 'column', gap: 2 },
  inviteRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: '1px solid #f1f5f9', fontSize: 13.5 },
  statusPill: { fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap' },
  ok: { color: '#16a34a', fontSize: 13, margin: '12px 0 0', fontWeight: 600 },
  muted: { color: '#94a3b8', fontSize: 14, margin: 0 },
  error: { color: '#dc2626', fontSize: 13, marginTop: 12, marginBottom: 0 },
};
